import Phaser from 'phaser';
import { ACTIONS, type Action, type ActionState, emptyActionState } from './actions';

// ControlMap — the single abstraction binding the Arcade action set to keyboard,
// on-screen touch, and the Gamepad API. Each source reports which Actions are
// currently held; the ControlMap ORs them and derives pressed/released edges so
// gameplay reads one uniform surface. Adding iOS controller glue later (the
// brother's WKWebView first-responder task) is just another Source.

/** Anything that can report the currently-held Actions for a frame. */
export interface ControlSource {
  /** Called once per frame. Return the set of Actions this source holds now. */
  poll(): Set<Action>;
}

// ── Keyboard ────────────────────────────────────────────────────────────────
// Arrow keys + WASD walk the belt in all four directions; Space jumps; J / X
// attacks. Up/W no longer jump — up is depth now, and the two cannot share a key.
export class KeyboardSource implements ControlSource {
  private keys: Record<string, Phaser.Input.Keyboard.Key>;

  constructor(kb: Phaser.Input.Keyboard.KeyboardPlugin) {
    const K = Phaser.Input.Keyboard.KeyCodes;
    this.keys = {
      left: kb.addKey(K.LEFT),
      a: kb.addKey(K.A),
      right: kb.addKey(K.RIGHT),
      d: kb.addKey(K.D),
      up: kb.addKey(K.UP),
      w: kb.addKey(K.W),
      down: kb.addKey(K.DOWN),
      s: kb.addKey(K.S),
      space: kb.addKey(K.SPACE),
      j: kb.addKey(K.J),
      x: kb.addKey(K.X),
    };
    // Stop Space / arrows scrolling the page while the canvas has focus.
    kb.addCapture([K.LEFT, K.RIGHT, K.UP, K.DOWN, K.SPACE]);
  }

  poll(): Set<Action> {
    const s = new Set<Action>();
    if (this.keys.left.isDown || this.keys.a.isDown) s.add('left');
    if (this.keys.right.isDown || this.keys.d.isDown) s.add('right');
    if (this.keys.up.isDown || this.keys.w.isDown) s.add('up');
    if (this.keys.down.isDown || this.keys.s.isDown) s.add('down');
    if (this.keys.space.isDown) s.add('jump');
    if (this.keys.j.isDown || this.keys.x.isDown) s.add('attack');
    return s;
  }
}

// ── Gamepad ───────────────────────────────────────────────────────────────
// Polls the raw W3C Gamepad API via navigator.getGamepads() so ANY paired
// controller reporting the "standard" mapping drives the slice, independent of
// Phaser's gamepad plugin connection timing. Standard mapping:
//   axes[0]/[1] = left stick X/Y, buttons[0] = A (jump), buttons[2] = X (attack),
//   buttons[12..15] = dpad up/down/left/right.
export class GamepadSource implements ControlSource {
  /** Stick deadzone — below this the axis is ignored so a resting stick doesn't
   *  creep the Actor. */
  private readonly deadzone = 0.35;

  poll(): Set<Action> {
    const s = new Set<Action>();
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads
      ? navigator.getGamepads()
      : [];
    for (const pad of pads) {
      if (!pad) continue;
      const ax = pad.axes[0] ?? 0;
      const ay = pad.axes[1] ?? 0;
      if (ax < -this.deadzone) s.add('left');
      if (ax > this.deadzone) s.add('right');
      if (ay < -this.deadzone) s.add('up');
      if (ay > this.deadzone) s.add('down');
      const btn = (i: number): boolean => !!pad.buttons[i]?.pressed;
      if (btn(14)) s.add('left');   // dpad left
      if (btn(15)) s.add('right');  // dpad right
      if (btn(12)) s.add('up');     // dpad up — depth, not jump
      if (btn(13)) s.add('down');   // dpad down
      if (btn(0)) s.add('jump');    // A
      if (btn(2) || btn(1) || btn(3)) s.add('attack'); // X / B / Y
    }
    return s;
  }

  /** True when at least one gamepad is currently connected — for the HUD hint. */
  static anyConnected(): boolean {
    const pads = typeof navigator !== 'undefined' && navigator.getGamepads
      ? navigator.getGamepads()
      : [];
    return pads.some((p) => !!p);
  }
}

// ── Touch (virtual) ─────────────────────────────────────────────────────────
// The on-screen HUD buttons write directly into this source's held set. Purely
// a data sink; the HUD owns the DOM/pointer wiring.
export class TouchSource implements ControlSource {
  private held = new Set<Action>();

  set(action: Action, down: boolean): void {
    if (down) this.held.add(action);
    else this.held.delete(action);
  }

  poll(): Set<Action> {
    return new Set(this.held);
  }
}

// ── The map ───────────────────────────────────────────────────────────────
export class ControlMap {
  private sources: ControlSource[] = [];
  private state: Record<Action, ActionState>;
  private prevDown: Record<Action, boolean>;

  constructor(sources: ControlSource[] = []) {
    this.sources = sources;
    this.state = Object.fromEntries(
      ACTIONS.map((a) => [a, emptyActionState()]),
    ) as Record<Action, ActionState>;
    this.prevDown = Object.fromEntries(ACTIONS.map((a) => [a, false])) as Record<Action, boolean>;
  }

  addSource(source: ControlSource): void {
    this.sources.push(source);
  }

  /** Recompute every Action's down/pressed/released for this frame. Call once at
   *  the top of the Scene update, before any Actor reads it. */
  update(): void {
    const down: Record<Action, boolean> = Object.fromEntries(
      ACTIONS.map((a) => [a, false]),
    ) as Record<Action, boolean>;

    for (const src of this.sources) {
      for (const a of src.poll()) down[a] = true;
    }

    for (const a of ACTIONS) {
      const was = this.prevDown[a];
      const now = down[a];
      this.state[a].down = now;
      this.state[a].pressed = now && !was;
      this.state[a].released = !now && was;
      this.prevDown[a] = now;
    }
  }

  get(action: Action): ActionState {
    return this.state[action];
  }

  /** Signed along-street intent: -1 left, +1 right, 0 none (both cancels). */
  axisX(): number {
    const l = this.state.left.down ? 1 : 0;
    const r = this.state.right.down ? 1 : 0;
    return r - l;
  }

  /** Signed into-street intent: -1 up (away), +1 down (toward camera). */
  axisZ(): number {
    const u = this.state.up.down ? 1 : 0;
    const d = this.state.down.down ? 1 : 0;
    return d - u;
  }
}
